import type { FunctionComponent } from 'react';
import { identify, trackEvent } from '@fff-web/fff-analytics';
import { useUserContext } from '@fff-web/fff-utilities';
import invalidateCaches from '../../../helpers/invalidateCaches';
import { reactivateCancelledSubscription } from '../../../services/subscription';
import { redirectToBaseSite } from '../../../utils/redirectMethods';
import { trackReactivation } from '../../../utils/tracking/subscription';
import * as styles from './reactivate-button.module.scss';

type ReactivateButtonProps = {
  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;
  setShowRetry: (showRetry: boolean) => void;
  text: string;
};

const ReactivateButton: FunctionComponent<ReactivateButtonProps> = ({
  isLoading,
  setIsLoading,
  setShowRetry,
  text = 'REACTIVATE NOW',
}) => {
  const { userInfo } = useUserContext();

  const handleOnClick = () => {
    setIsLoading(true);
    trackEvent({
      ctaName: 'Cancelled Reactivates',
      properties: { coupon_code: '' },
    });

    const sendSegmentEventAfterResponse = (status: string, callback: () => void) => {
      trackEvent({
        ctaName: 'Cancelled Reactivates Result',
        properties: { coupon_code: '', status },
      })
        .then(callback)
        .catch((e) => {
          console.warn(e);
          callback();
        });
    };

    reactivateCancelledSubscription()
      .then((response) => {
        trackReactivation({ response, user: userInfo.user });
        if (response?.ok) {
          sendSegmentEventAfterResponse('success', async () => {
            // Awaited so the server-side BFF cache bust settles before we navigate
            // away — a bare call would let the redirect cancel the in-flight DELETE.
            await invalidateCaches();
            identify();
            redirectToBaseSite();
          });
        } else {
          sendSegmentEventAfterResponse('failure', () => {
            console.error('An error had happened', response);
          });
          setIsLoading(false);
          setShowRetry(true);
        }
      })
      .catch((error) => {
        sendSegmentEventAfterResponse('failure', () => {
          console.error(error);
        });
        setIsLoading(false);
      });
  };

  return (
    <button
      type="button"
      className={styles['confirmReactivate']}
      onClick={handleOnClick}
      disabled={isLoading}
    >
      {text}
    </button>
  );
};

export default ReactivateButton;
